import { useCallback, useEffect, useMemo, useState } from "react";
import { deleteGeneratedFile, errorMessage, listGeneratedFiles } from "../api";
import type { StoredFile } from "../types";
import { useDashboard } from "./Dashboard";

/** What the page needs to render. One call's worth. */
export interface FileListing {
  retentionDays: number;
  files: StoredFile[];
}

interface Props {
  /**
   * Where the listing comes from. Defaults to the callable, which is what the app uses.
   *
   * It is a prop at all for `preview.tsx`, which renders the real pages against mock data
   * with no Firebase session -- every other page reads what it needs from the outlet
   * context and so needs no seam, but this one fetches. Three lines here beats either a
   * page the layout harness cannot show or a second, drifting copy of its markup.
   */
  load?: (projectId: string) => Promise<FileListing>;
}

async function fetchFiles(projectId: string): Promise<FileListing> {
  const res = await listGeneratedFiles({ projectId });
  return res.data;
}

/**
 * What the tools have made: the files a tool generated and stored rather than handed back.
 *
 * A page of its own for the same reason Tools is one. /providers answers "which accounts
 * are on", /tools answers "what can they do" — and neither has anywhere to put "what did
 * they produce". The question has no account in it either: someone looking for a track
 * wants every track, not one card's worth, which is exactly the case a per-card panel
 * cannot serve.
 *
 * It is the one page here that fetches instead of subscribing. Everything else on the
 * dashboard is a Firestore listener; these files live in a bucket, and the bucket is the
 * only record of them (see functions/src/storage.ts on why there is no mirror). So there
 * is a Refresh button, which is the honest version of a list that cannot push.
 */
export default function Files({ load: fetcher = fetchFiles }: Props = {}) {
  const { project, catalog, notify } = useDashboard();

  const [files, setFiles] = useState<StoredFile[]>([]);
  const [retentionDays, setRetentionDays] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  /** Set when the listing itself failed — usually "Cloud Storage was never enabled". */
  const [failure, setFailure] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(
    async (projectId: string) => {
      setLoading(true);
      try {
        const listing = await fetcher(projectId);
        setFiles(listing.files);
        setRetentionDays(listing.retentionDays);
        setFailure(null);
      } catch (err) {
        // Kept on the page rather than fired as a toast: this is the whole content, and
        // a toast about it disappears while the empty page stays and explains nothing.
        setFailure(errorMessage(err));
        setFiles([]);
      } finally {
        setLoading(false);
      }
    },
    [fetcher]
  );

  useEffect(() => {
    if (!project) return;
    setFiles([]);
    void load(project.id);
  }, [project?.id, load]);

  const iconFor = (providerId: string | null) =>
    catalog.find((p) => p.id === providerId)?.icon ?? "📄";

  /**
   * Name filter over everything a row shows, the prompt included.
   *
   * The prompt matters more than the filename here: a generated track is named after a
   * slug of its own title, so "the one about the rain" is findable only through the words
   * that made it. Same instant, no-request filter as the one on /providers and /tools.
   */
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return files;
    return files.filter((file) =>
      `${file.name} ${file.accountLabel ?? ""} ${file.slug ?? ""} ${Object.values(
        file.meta
      ).join(" ")}`
        .toLowerCase()
        .includes(needle)
    );
  }, [files, query]);

  const totalBytes = useMemo(
    () => files.reduce((sum, file) => sum + file.bytes, 0),
    [files]
  );

  async function remove(file: StoredFile) {
    if (
      !confirm(
        `Delete ${file.meta.title || displayName(file.name)}?\n\n` +
          `This cannot be undone, and any link to it stops working immediately.`
      )
    ) {
      return;
    }

    setBusy(file.name);
    try {
      await deleteGeneratedFile({ connectionId: file.connectionId, name: file.name });
      // Dropped locally rather than by refetching: the delete is the answer, and a second
      // round trip would only tell us what we already know.
      setFiles((current) => current.filter((f) => f.name !== file.name));
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="section">
      <div className="section-head">
        <div>
          <h2>Files</h2>
          <p className="section-sub">
            What your tools have generated and stored — audio from Lyria today, anything
            too large to hand back inline in future. Files from a team account are visible
            to the whole team; files from a private account are only yours.
            {retentionDays !== null && (
              <>
                {" "}
                Each one is deleted {retentionDays} days after it was made, and its link
                stops working then — download anything worth keeping.
              </>
            )}
          </p>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => project && void load(project.id)}
          disabled={loading || !project}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {failure ? (
        <div className="empty">
          <p>Could not list stored files.</p>
          <p className="empty-sub">{failure}</p>
        </div>
      ) : loading && files.length === 0 ? (
        <div className="empty">
          <p>Loading…</p>
        </div>
      ) : files.length === 0 ? (
        <div className="empty">
          <p>Nothing stored yet.</p>
          <p className="empty-sub">
            Tools that produce a file rather than an answer put it here. Connect Lyria on
            the Providers page and ask your AI client to generate a track.
          </p>
        </div>
      ) : (
        <>
          <div className="search-bar">
            <input
              className="search-input"
              value={query}
              placeholder="Filter by name, account, or the prompt that made it…"
              aria-label="Filter files"
              spellCheck={false}
              autoComplete="off"
              onChange={(e) => setQuery(e.target.value)}
            />
            <span className="search-count">
              {shown.length === files.length
                ? `${files.length} file${files.length === 1 ? "" : "s"} · ${formatBytes(
                    totalBytes
                  )}`
                : `${shown.length} of ${files.length}`}
            </span>
          </div>

          <ul className="files">
            {shown.map((file) => (
              <li key={`${file.connectionId}/${file.name}`} className="file-row">
                <div className="file-main">
                  <span className="file-icon">{iconFor(file.providerId)}</span>
                  <div className="file-headline">
                    <span className="file-title">
                      {file.meta.title || displayName(file.name)}
                    </span>
                    <span className="file-meta">
                      {file.accountLabel ? (
                        <span className="pill pill-xs">{file.slug}</span>
                      ) : (
                        // The account is gone but the object is not: it lives until
                        // retention reaches it, so say so rather than showing a blank.
                        <span className="pill pill-xs pill-error">disconnected</span>
                      )}
                      <span>{formatBytes(file.bytes)}</span>
                      <span>{relativeTime(file.createdAt)}</span>
                      <span className="file-expiry">{expiryLabel(file.expiresAt)}</span>
                    </span>
                  </div>
                  <div className="file-actions">
                    {file.url && (
                      <a
                        className="btn btn-ghost btn-sm"
                        href={file.url}
                        download={displayName(file.name)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Download
                      </a>
                    )}
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => void remove(file)}
                      disabled={busy === file.name}
                    >
                      {busy === file.name ? "Deleting…" : "Delete"}
                    </button>
                  </div>
                </div>

                {/* The point of the page. A row that only *describes* a song is a row
                    you have to leave the page to use; the browser plays audio natively,
                    so play it here. `preload="none"` because a folder of tracks would
                    otherwise fetch every one of them on load. */}
                {file.url && file.contentType.startsWith("audio/") && (
                  <audio
                    className="file-audio"
                    src={file.url}
                    controls
                    preload="none"
                  />
                )}

                {file.meta.prompt && (
                  <p className="file-prompt">
                    {file.meta.model && (
                      <span className="file-model">{file.meta.model}</span>
                    )}
                    {file.meta.prompt}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/**
 * The filename with the uniqueness stripped off the front.
 *
 * Objects are named `<uuid>-<slug>.<ext>` so two tracks called the same thing are two
 * objects (see `storeGeneratedFile`). The uuid is load-bearing in the bucket and pure
 * noise in a list -- at 360px it is the whole width of the row and pushes the part a
 * person recognises off the end. Only the real object name is ever sent back for a
 * delete; this is display only.
 */
function displayName(name: string): string {
  return name.replace(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/,
    ""
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function relativeTime(iso: string): string {
  const seconds = Math.floor((Date.now() - Date.parse(iso)) / 1000);
  if (!Number.isFinite(seconds) || seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * How long is left, in the words that matter.
 *
 * Retention is the one thing about these files a user has to act on — the link dies with
 * the object — so this counts down rather than printing the date it will happen.
 */
function expiryLabel(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return "expired";
  const hours = Math.floor(ms / 1000 / 60 / 60);
  if (hours < 1) return "expires within the hour";
  if (hours < 24) return `expires in ${hours}h`;
  const days = Math.round(hours / 24);
  return `expires in ${days} day${days === 1 ? "" : "s"}`;
}
